import fetch from "node-fetch"
import { HttpsProxyAgent } from "https-proxy-agent"
import { logger } from "@/src/utils/logger"
import { spinner } from "@/src/utils/spinner"
import { colors } from "@/src/utils/colors"
import { tokenStorage } from "@/src/utils/bootstrap"
import { saveAuthToken, type PackageManager } from "./package-manager-config"
import { REGISTRY_URL } from "./registry"

const AUTH_API_URL = `${REGISTRY_URL}/api/cli/v1`

type LoginResponse = {
  token: string
  authToken: string
}

type PlanResponse = Array<{
  name: string
}>

type AuthTokens = {
  bearerToken: string
  authToken: string
}

export type AuthResult = {
  success: boolean
  tokens?: AuthTokens
  error?: string
}

export type AuthStatus = {
  authenticated: boolean
  user?: string
  plans?: string[]
  expires?: string | null
  token?: string
}

const httpAgent = process.env.https_proxy
  ? new HttpsProxyAgent(process.env.https_proxy)
  : undefined

async function getApiErrorMessage(response: {
  clone: () => { json: () => Promise<unknown> }
}): Promise<string | null> {
  try {
    const payload = await response.clone().json()

    if (
      payload &&
      typeof payload === "object" &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }

    if (
      payload &&
      typeof payload === "object" &&
      "message" in payload &&
      typeof payload.message === "string"
    ) {
      return payload.message
    }
  } catch {
    return null
  }

  return null
}

function getAuthErrorMessage(
  response: { status: number },
  apiErrorMessage?: string | null
): string {
  switch (response.status) {
    case 400:
      return "The authentication request was not accepted. Please check your email and password, then try again."
    case 401:
      return "Invalid email or password. Please check your credentials and try again."
    case 403:
      return "Your account is not allowed to use registry authentication. Please check your Tiptap Cloud account access."
    case 429:
      return "Too many login attempts. Please wait a moment and try again."
    default:
      if (apiErrorMessage && response.status >= 500) {
        return apiErrorMessage
      }

      if (response.status >= 500) {
        return "Tiptap authentication is temporarily unavailable. Please try again later."
      }

      return "Authentication failed. Please try again."
  }
}

function getPlansErrorMessage(
  response: { status: number },
  apiErrorMessage?: string | null
): string {
  switch (response.status) {
    case 401:
    case 403:
      return "Your login succeeded, but your session could not be verified. Please run tiptap login again."
    case 429:
      return "Your plans could not be loaded because there were too many requests. Please wait a moment and try again."
    default:
      if (apiErrorMessage && response.status >= 500) {
        return apiErrorMessage
      }

      if (response.status >= 500) {
        return "Your plans could not be loaded because Tiptap Cloud is temporarily unavailable. Please try again later."
      }

      return "Your plans could not be loaded. Please try again."
  }
}

/**
 * Authenticate a user with the Tiptap registry
 */
export async function authenticateUser({
  email,
  password,
  packageManager,
  writeConfig = true,
  cwd,
}: {
  email?: string
  password?: string
  packageManager: PackageManager
  writeConfig?: boolean
  cwd: string
}): Promise<AuthResult> {
  const loginSpinner = spinner(
    "Authenticating with Tiptap registry..."
  )?.start()

  try {
    if (!email || !password) {
      loginSpinner?.fail("Authentication failed")
      return { success: false, error: "Invalid credentials" }
    }

    const tokens = await requestAuthTokens(email, password)

    let userPlans: string[] = []
    const plansResponse = await fetch(`${AUTH_API_URL}/plans`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.bearerToken}`,
      },
      agent: httpAgent,
    })

    if (!plansResponse.ok) {
      loginSpinner?.fail("Authentication failed (could not load user plans)")
      const apiErrorMessage = await getApiErrorMessage(plansResponse)
      return {
        success: false,
        error: getPlansErrorMessage(plansResponse, apiErrorMessage),
      }
    }

    const plans = (await plansResponse.json()) as PlanResponse
    userPlans = plans.map((plan) => plan.name)

    if (!userPlans.length) {
      loginSpinner?.fail("Authentication failed (no active plans)")
      return { success: false, error: "No active plans found for user" }
    }

    tokenStorage.setBearerToken(tokens.bearerToken, {
      email,
      plans: userPlans,
    })
    tokenStorage.setAuthToken(tokens.authToken)

    if (writeConfig) {
      const success = await saveAuthToken(tokens.authToken, packageManager, cwd)
      if (!success) {
        loginSpinner?.fail("Failed to save authentication token")
        return { success: false, error: "Could not save authentication token" }
      }
    }

    loginSpinner?.stopAndPersist({
      symbol: colors.cyan("✔"),
      text: "Authentication successful",
    })
    return { success: true, tokens }
  } catch (error) {
    loginSpinner?.fail("Authentication failed")
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error during authentication",
    }
  }
}

/**
 * Request an authentication token from the API
 */
async function requestAuthTokens(
  email: string,
  password: string
): Promise<AuthTokens> {
  const response = await fetch(`${AUTH_API_URL}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
    agent: httpAgent,
  })

  if (!response.ok) {
    const apiErrorMessage = await getApiErrorMessage(response)
    throw new Error(getAuthErrorMessage(response, apiErrorMessage))
  }

  const data = (await response.json()) as LoginResponse

  return {
    bearerToken: data.token,
    authToken: data.authToken,
  }
}

/**
 * Check if user is authenticated with Tiptap registry
 */
export async function checkAuthStatus(): Promise<AuthStatus> {
  try {
    const bearerToken = tokenStorage.getBearerToken()

    if (!bearerToken) {
      return { authenticated: false }
    }

    if (!tokenStorage.isValidToken()) {
      return { authenticated: false }
    }

    // Use plans API to verify token is still valid
    let userPlans: string[] = []
    try {
      const plansResponse = await fetch(`${AUTH_API_URL}/plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        agent: httpAgent,
      })

      if (!plansResponse.ok) {
        tokenStorage.clear()
        return { authenticated: false }
      }

      const plans = (await plansResponse.json()) as PlanResponse
      userPlans = plans.map((plan) => plan.name)

      // Update stored plans
      const userInfo = tokenStorage.getUserInfo()
      tokenStorage.setBearerToken(bearerToken, {
        email: userInfo.email,
        plans: userPlans,
      })
    } catch (error) {
      tokenStorage.clear()
      return { authenticated: false }
    }

    const userInfo = tokenStorage.getUserInfo()
    const expiration = tokenStorage.getTokenExpiration()

    return {
      authenticated: true,
      user: userInfo.email || "unknown user",
      plans: userPlans,
      expires: expiration,
      token: bearerToken,
    }
  } catch (error) {
    logger.error(
      `Auth status check error: ${error instanceof Error ? error.message : "Unknown error"}`
    )
    return { authenticated: false }
  }
}

/**
 * Logout user from Tiptap registry
 */
export async function logoutUser(): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const bearerToken = tokenStorage.getBearerToken()

    if (bearerToken) {
      const response = await fetch(`${AUTH_API_URL}/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        agent: httpAgent,
      })

      if (!response.ok) {
        logger.warn("Logout API call failed, but clearing local tokens anyway")
      }
    }

    tokenStorage.clear()
    return { success: true }
  } catch (error) {
    tokenStorage.clear()
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Unknown error during logout",
    }
  }
}

/**
 * Check if user has a specific plan
 */
export function userHasPlan(planName: string): boolean {
  return tokenStorage.hasPlan(planName)
}

/**
 * Check if user has any of the specified plans
 */
export function userHasAnyPlan(planNames: string[]): boolean {
  return planNames.some((plan) => tokenStorage.hasPlan(plan))
}

/**
 * Get user's plans
 */
export function getUserPlans(): string[] {
  return tokenStorage.getUserInfo().plans || []
}
